import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { DollarSign, Save } from 'lucide-react';
import { toast } from 'sonner';

const AddIncome = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState('Salary');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [incomeCategories, setIncomeCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/income');
        const categoryNames = response.data.map(c => c.name);
        setIncomeCategories(categoryNames);
        if (categoryNames.length > 0 && !categoryNames.includes(category)) {
          setCategory(categoryNames[0]);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setIncomeCategories(['Salary', 'Freelance', 'Gift', 'Refund', 'Other']);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert date to MM/DD/YYYY format
      const dateObj = new Date(date);
      const formattedDate = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${String(dateObj.getDate()).padStart(2, '0')}/${dateObj.getFullYear()}`;

      await api.post('/income', {
        amount: parseFloat(amount),
        date: formattedDate,
        category,
        description: description || null,
      });

      toast.success('Income added successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error adding income:', error);
      toast.error('Failed to add income');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
            Add Income
          </h1>
          <p className="text-lg text-muted-foreground">
            Record your income to track your complete financial picture
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl space-y-6">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Amount (USD) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="input-amount"
                placeholder="0.00"
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Date *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="input-date"
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="select-category"
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {incomeCategories.map((cat) => (
                <option key={cat} value={cat} className="bg-[#0f172a]">
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="input-description"
              placeholder="e.g., January salary, Freelance project for ABC Corp"
              rows="3"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              data-testid="cancel-button"
              className="flex-1 bg-white/10 text-white hover:bg-white/20 px-6 py-3 rounded-full font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="save-button"
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>{loading ? 'Saving...' : 'Save Income'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddIncome;
